import { AutogenLayoutTruckOrderNotUsed } from "./autogen/AutogenLayoutTruckOrderNotUsed";
import {
    BlurEvent, ClickEvent, DataSourceMode, Label, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, Panel,
    SaveButton, SlideoutDecorator, Snackbar, Toast
} from "@mcleod/components";
import {
    Currency, CurrencySettings, FieldUpdateEvent, HorizontalAlignment, Model, ModelRow, StringUtil, CurrencyUtil
} from "@mcleod/core";
import { OrderTabView } from "./OrderTabView";

export interface TonuSlideoutProps {
    orderId: string;
    driverId?: string;
    doAfterSave?: () => void;
}

export class TruckOrderNotUsed extends AutogenLayoutTruckOrderNotUsed {
    private saveButton: SaveButton = null;
    private isMultiCurrencyEnabled: boolean = CurrencySettings.getSingleton()?.multiCurrencyEnabled();

    public static openSlideout(props: TonuSlideoutProps) {
        Model.searchSingleRecord("lme/dispatch/tonu", {"order_id": props.orderId, "driver_id": props.driverId, "bill_for_tonu": true, "pay_driver": true}).then((response) => {
            const layout = Layout.getLayout("lme/dispatch/TruckOrderNotUsed") as TruckOrderNotUsed;
            layout.init(response, props);
            new SlideoutDecorator({
                layout: layout,
                title: `Truck Order Not Used - ${props.orderId}`,
                overlayProps: {closeOnClickOff: false, greyedBackground: true},
                width: 1100,
                fillVerticalSpace: true,
                addlComponents: layout.saveButton,
            });
        }).catch((error) => {
            Snackbar.showWarningSnackbar(error.messages);
        });
    }

    public init(modelRow: ModelRow, props: TonuSlideoutProps) {
        modelRow._appending = true;
        const data = [modelRow]
        this.getSaveButton(props.doAfterSave)
        this.addLayoutLoadListener(async (event) => {
            this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, data, null);
            this.activeRow.getMetadata().keyFields = ["order_id"];
            this.activeRow.set("order_id", props.orderId);
            this.setBillForTonuVisibility(true);
            this.setPayDriverVisibility(true);
            this.addFieldUpdateListeners();
            this.saveButton.enabled = true;
            this.textboxOtherChargeCurrencyType.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
            CurrencyUtil.configureCurrencySelector(this.textboxOtherChargeCurrencyType);
            this.textboxDeductCurrencyType.visible = CurrencySettings.getSingleton().multiCurrencyEnabled();
            CurrencyUtil.configureCurrencySelector(this.textboxDeductCurrencyType);
        });
    }

    setBillForTonuVisibility(value: boolean) {
        this.textboxOtherChargeCode.visible = value;
        this.textboxOtherChargeCode.required = value;
        this.textboxOtherChargeAmount.visible = value;
        this.textboxOtherChargeAmount.required = value;
        this.textboxBillingComment.visible = value;
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.textboxOtherChargeCurrencyType.visible = value;
        }
    }

    setPayDriverVisibility(value: boolean) {
        this.textboxDriverId.visible = value;
        this.textboxDriverId.required = value;
        this.textboxDeductCode.visible = value;
        this.textboxDeductCode.required = value;
        this.textboxDeductAmount.visible = value;
        this.textboxDeductAmount.required = value;
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.textboxDeductCurrencyType.visible = value;
        }
    }

    async search(searchObject?: any): Promise<void> {
        this.saveButton.busy = true;
        try {
            await this.sourceTruckOrderNotUsed.search({...this.activeRow.data, ...searchObject});
            this.addFieldUpdateListeners();
            this.sourceTruckOrderNotUsed.data[0]._appending = true;
        } finally {
            this.saveButton.busy = false;
        }
    }

    addFieldUpdateListeners() {
        this.activeRow.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
            if (event.fieldName === "bill_for_tonu") {
                this.setBillForTonuVisibility(event.newValue);
            } else if ((event.fieldName === "pay_driver")) {
                this.setPayDriverVisibility(event.newValue);
            }

            this.saveButton.enabled = !(!event.row.getBoolean("bill_for_tonu", false)
                && !event.row.getBoolean("pay_driver", false));
        });
    }

    textboxOtherChargeCodeBeforeLookupModelSearch (event: LookupModelSearchEvent) {
        event.filter.is_tonu = "Y";
    }

    textboxDeductCodeBeforeLookupModelSearch (event: LookupModelSearchEvent) {
        event.filter.code_type = "E";
        event.filter.is_active = "Y";
    }

    textboxDriverIdBeforeLookupModelSearch (event: LookupModelSearchEvent) {
        event.filter.payee_id = ">";
        event.filter.is_active = "Y";
        event.filter["payee.status"] = "A";
    }

    async textboxDriverIdOnLookupModelSelection (event: LookupModelSelectionEvent) {
        if (event.selection != null)
            await this.search({ driver_id: event.selection.getString("id") });
    }

    async textboxOtherChargeCodeAfterLookupSelection (event: LookupModelSelectionEvent) {
        if (event.selection == null) return;
        await this.search({
            other_charge_code: event.selection.getString("id"),
            other_charge_amount: null,
        });
    }

    textboxOtherChargeAmountOnBlur (event: BlurEvent) {
        const otherChargeAmount = this.activeRow.getCurrency("other_charge_amount")?.getAmount();
        if (otherChargeAmount == null || isNaN(otherChargeAmount) || otherChargeAmount <= 0) {
            return;
        }
        const deductMethod = this.activeRow.getString("deduct_method");
        if ("A" === deductMethod) {
            this.activeRow.set("deduct_amount", this.activeRow.getCurrency("other_charge_amount"));
        } else if ("P" === deductMethod) {
            const percent = this.activeRow.getNumber("deduct_percent") / 100;
            const amount = this.activeRow.getCurrency("other_charge_amount").getAmount() * percent;
            this.activeRow.set("deduct_amount", new Currency(amount));
        }
    }

    private getSaveButton(doAfterSave: ()=>void) {
        this.saveButton = SaveButton.create({
            id: "saveButton",
            caption: "Save",
            rowBreak: false,
            enabled: false,
            width: 75,
            backgroundColor: "primary",
            color: "primary.reverse",
            borderWidth: 0,
            onClick: async (event: ClickEvent) => {
                this.saveButton.busy = true;
                if (!this.validate()) {
                    this.saveButton.busy = false;
                    return;
                }
                this.sourceTruckOrderNotUsed.post().then(_ => {
                    this.slideOut().then(() => {
                        doAfterSave?.()
                    });
                }).finally(() => {
                    this.saveButton.busy = false;
                });
            }
        });
    }

    static showSuccessToast(orderId: string, orderClickNewTab: boolean = true) {
        Toast.showToast(this.getSuccessPanel(orderId, orderClickNewTab));
    }

    private static getSuccessPanel(orderId: string, orderClickNewTab: boolean): Panel {
        const labelProps = {
            color: "white",
            fontSize: "xlarge",
            rowBreak: false,
            paddingLeft: 1,
            paddingRight: 1,
            marginLeft: 0,
            marginRight: 0
        };
        let labels: Label[] = [];
        if (!StringUtil.isEmptyString(orderId)) {
            labels = [
                new Label({ ...labelProps, text: "Truck Order Not Used successfully processed for Order " }),
                new Label({
                    ...labelProps,
                    style: { textDecoration: "underline" },
                    text: orderId,
                    onClick: _ => OrderTabView.navigateTo({key: orderId, newTab: orderClickNewTab}),
                }),
            ];
        }
        return new Panel({ align: HorizontalAlignment.CENTER, fillRow: true, components: labels });
    }

    textboxOtherChargeCurrencyTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.updateCurrencyCode(event, "other_charge_amount");
    }

    textboxDeductCurrencyTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.updateCurrencyCode(event, "deduct_amount");
    }

    updateCurrencyCode(event: LookupModelSelectionEvent, field: string) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        const selectedRow = event.selection;
        if (selectedRow) {
            const currencyType = selectedRow.get("currency_type_id", CurrencySettings.getTransactionalCurrency()) as string;
            let currency = this.activeRow.getCurrency(field);
            if (!currency) {
                currency = new Currency(0)
            }
            currency.setCurrencyCode(currencyType);
            this.activeRow.updateCurrency(field, currency);
        }
    }

    textboxDeductAmountOnBlur(event: BlurEvent) {
        if (!this.isMultiCurrencyEnabled) return;
        const currencyCode = this.activeRow?.getCurrency("deduct_amount")?.currency_code;
        this.activeRow?.set("deduct_amount_c", currencyCode);
        this.activeRow?.setLookupModelData("deduct_amount_c", { currency_type_id: currencyCode });
        this.mainDataSource.displayDataInBoundComponent(this.textboxDeductCurrencyType);
    }
}
